import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Search, Loader2, ChevronLeft, ChevronRight, RefreshCw } from 'lucide-react';
import {
  getTrafficAnalytics, getSupervisionFeed, getUserSupervision,
  type TrafficBucket, type SupervisionRow, type UserSupervision,
} from '../../services/adminService';
import Markdown from '../chatHome/Markdown';

const usd = (n: number) => `$${(n || 0).toFixed(2)}`;
const num = (n: number) => (n || 0).toLocaleString();

/** 7d / 30d range toggle — shared across the supervision views. */
const RangeToggle: React.FC<{ value: 7 | 30; onChange: (v: 7 | 30) => void }> = ({ value, onChange }) => (
  <div className="flex gap-1">
    {([7, 30] as const).map((v) => (
      <button key={v} onClick={() => onChange(v)}
        className={`rounded-md px-2.5 py-1 text-xs font-medium ${value === v ? 'bg-secondary-900 text-white dark:bg-white dark:text-secondary-900' : 'text-secondary-500 hover:bg-secondary-100 dark:hover:bg-secondary-800'}`}>
        {v}d
      </button>
    ))}
  </div>
);

/** A summary stat cell. */
const Stat: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div className="rounded-xl border border-secondary-200 dark:border-secondary-800 px-3 py-2">
    <div className="text-[11px] uppercase tracking-wide text-secondary-400">{label}</div>
    <div className="text-lg font-semibold text-secondary-900 dark:text-white">{value}</div>
  </div>
);

/** Monochrome daily-requests bars (no chart lib — stays on-palette). */
const TrafficBars: React.FC<{ buckets: TrafficBucket[] }> = ({ buckets }) => {
  const list = Array.isArray(buckets) ? buckets : [];
  const max = Math.max(1, ...list.map((b) => b.requests || 0));
  return (
    <div className="flex items-end gap-1 h-28" data-testid="traffic-bars">
      {list.map((b) => (
        <div key={b.day} className="flex-1 flex flex-col items-center justify-end group" title={`${b.day}: ${num(b.requests)} requests · ${num(b.users)} users`}>
          <div className="w-full rounded-t bg-secondary-800 dark:bg-secondary-200 group-hover:bg-secondary-900 dark:group-hover:bg-white transition-colors"
            style={{ height: `${Math.round((b.requests / max) * 100)}%`, minHeight: b.requests ? 2 : 0 }} />
        </div>
      ))}
    </div>
  );
};

/** A response cell — Markdown-rendered (like the chat bubble), collapsed by default with a show-full toggle. */
const ResponseCell: React.FC<{ text: string }> = ({ text }) => {
  const [open, setOpen] = useState(false);
  const long = (text || '').length > 220;
  return (
    <td className="text-secondary-600 dark:text-secondary-300 max-w-[420px] align-top">
      <div className={`text-sm break-words ${open ? '' : 'max-h-24 overflow-hidden relative'}`}>
        <Markdown content={text || '—'} />
        {!open && long && <div className="pointer-events-none absolute inset-x-0 bottom-0 h-6 bg-gradient-to-t from-white dark:from-black" />}
      </div>
      {long && <button onClick={() => setOpen((o) => !o)} className="mt-1 text-xs text-secondary-400 hover:text-secondary-900 dark:hover:text-white">{open ? 'Show less' : 'Show full'}</button>}
    </td>
  );
};

/** A masked prompt→response feed: Markdown-rendered responses (truncate/expand), email search, pagination. */
const TurnsTable: React.FC<{ rows: SupervisionRow[]; showEmail?: boolean }> = ({ rows, showEmail }) => {
  const [q, setQ] = useState('');
  const [pageSize, setPageSize] = useState(10);
  const [page, setPage] = useState(0);
  const filtered = useMemo(() => {
    const list = Array.isArray(rows) ? rows : [];
    return showEmail && q.trim() ? list.filter((r) => (r.email || '').toLowerCase().includes(q.trim().toLowerCase())) : list;
  }, [rows, q, showEmail]);
  const pageCount = Math.max(1, Math.ceil(filtered.length / pageSize));
  const p = Math.min(page, pageCount - 1);
  const pageRows = filtered.slice(p * pageSize, p * pageSize + pageSize);
  useEffect(() => { setPage(0); }, [q, pageSize]);

  return (
    <div>
      {showEmail && (
        <div className="mb-2 flex items-center gap-2">
          <div className="relative flex-1 max-w-xs">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-secondary-400" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search email…"
              className="w-full rounded-md border border-secondary-300 dark:border-secondary-700 bg-white dark:bg-secondary-900 pl-8 pr-2 py-1.5 text-sm focus:outline-none focus:border-secondary-500" />
          </div>
          <span className="text-xs text-secondary-400">{filtered.length} turns</span>
        </div>
      )}
      {filtered.length === 0 ? (
        <p className="text-sm text-secondary-400">No prompts in this window.</p>
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-secondary-400 text-left text-xs uppercase tracking-wide">
                <tr>
                  <th className="py-1 pr-2">When</th>{showEmail && <th className="pr-2">User</th>}
                  <th className="pr-2">Agent</th><th className="pr-2">Prompt</th><th>Response</th>
                </tr>
              </thead>
              <tbody>
                {pageRows.map((r, i) => (
                  <tr key={p * pageSize + i} className="border-t border-secondary-100 dark:border-secondary-800 align-top">
                    <td className="py-1.5 pr-2 text-secondary-400 whitespace-nowrap">{r.createdAt ? new Date(r.createdAt).toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' }) : '—'}</td>
                    {showEmail && <td className="pr-2 text-secondary-400 whitespace-nowrap">{r.email}</td>}
                    <td className="pr-2 font-medium text-secondary-900 dark:text-white whitespace-nowrap">{r.agentName}<div className="text-[10px] font-normal text-secondary-400">{r.tier}</div></td>
                    <td className="pr-2 text-secondary-700 dark:text-secondary-200 max-w-[280px] align-top">{r.prompt || '—'}</td>
                    <ResponseCell text={r.response || ''} />
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="mt-3 flex flex-wrap items-center justify-between gap-2 text-xs text-secondary-500">
            <label className="flex items-center gap-1.5">Rows per page
              <select value={pageSize} onChange={(e) => setPageSize(Number(e.target.value))} className="rounded-md border border-secondary-300 dark:border-secondary-700 bg-white dark:bg-secondary-900 px-1.5 py-1">
                {[10, 25, 50].map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
            </label>
            <div className="flex items-center gap-2">
              <button onClick={() => setPage((x) => Math.max(0, x - 1))} disabled={p <= 0} className="p-1 rounded-md hover:bg-secondary-100 dark:hover:bg-secondary-800 disabled:opacity-30" aria-label="Previous page"><ChevronLeft className="w-4 h-4" /></button>
              <label className="flex items-center gap-1">Page
                <select value={p} onChange={(e) => setPage(Number(e.target.value))} className="rounded-md border border-secondary-300 dark:border-secondary-700 bg-white dark:bg-secondary-900 px-1.5 py-1">
                  {Array.from({ length: pageCount }, (_, i) => <option key={i} value={i}>{i + 1}</option>)}
                </select>
                of {pageCount}
              </label>
              <button onClick={() => setPage((x) => Math.min(pageCount - 1, x + 1))} disabled={p >= pageCount - 1} className="p-1 rounded-md hover:bg-secondary-100 dark:hover:bg-secondary-800 disabled:opacity-30" aria-label="Next page"><ChevronRight className="w-4 h-4" /></button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

/** Traffic analysis + global feed (share one range). */
const TrafficSection: React.FC = () => {
  const [days, setDays] = useState<7 | 30>(7);
  const [traffic, setTraffic] = useState<{ buckets: TrafficBucket[]; totals: TrafficBucket } | null>(null);
  const [feed, setFeed] = useState<SupervisionRow[]>([]);
  const [err, setErr] = useState('');
  const [refreshing, setRefreshing] = useState(false);
  // Monitoring data must reflect the LATEST activity on demand — this pulls fresh (the API is no-store, so
  // every call hits live data). Runs on mount + range change, and on the manual Refresh button.
  const reload = useCallback(async () => {
    setRefreshing(true);
    setErr('');
    try {
      const [tr, fd] = await Promise.all([getTrafficAnalytics(days), getSupervisionFeed(days, 200)]);
      setTraffic({ buckets: tr.buckets, totals: tr.totals });
      setFeed(fd.events);
    } catch (e) {
      setErr((e as { message?: string })?.message || 'Failed to load traffic');
    } finally {
      setRefreshing(false);
    }
  }, [days]);
  useEffect(() => { void reload(); }, [reload]);
  const t = traffic?.totals;
  return (
    <>
      <div className="rounded-2xl border border-secondary-200 dark:border-secondary-800 p-5">
        <div className="mb-3 flex items-center justify-between gap-2">
          <h2 className="font-semibold">Traffic — last {days} days</h2>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => void reload()}
              disabled={refreshing}
              title="Refresh with the latest activity"
              className="inline-flex items-center gap-1.5 rounded-lg border border-secondary-200 dark:border-secondary-700 px-2.5 py-1 text-xs text-secondary-600 dark:text-secondary-300 hover:bg-secondary-50 dark:hover:bg-secondary-800 disabled:opacity-50"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh
            </button>
            <RangeToggle value={days} onChange={setDays} />
          </div>
        </div>
        {err && <p className="text-sm text-red-500 mb-2">{err}</p>}
        {t && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-4">
            <Stat label="Requests" value={num(t.requests)} />
            <Stat label="Active users" value={num(t.users)} />
            <Stat label="Tokens" value={num(t.tokens)} />
            <Stat label="Est. cost" value={usd(t.costUsd)} />
          </div>
        )}
        {traffic && <TrafficBars buckets={traffic.buckets} />}
      </div>

      <div className="rounded-2xl border border-secondary-200 dark:border-secondary-800 p-5">
        <h2 className="font-semibold mb-3">Recent prompts &amp; responses <span className="text-xs font-normal text-secondary-400">· PII masked</span></h2>
        <TurnsTable rows={feed} showEmail />
      </div>
    </>
  );
};

/** Dive into a single user by email. */
const UserDrill: React.FC = () => {
  const [email, setEmail] = useState('');
  const [days, setDays] = useState<7 | 30>(30);
  const [data, setData] = useState<UserSupervision | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const run = useCallback(async () => {
    const q = email.trim();
    if (!q) return;
    setBusy(true); setErr(''); setData(null);
    try { setData(await getUserSupervision(q, days)); }
    catch (e: any) { setErr(e?.message || 'No account for that email.'); }
    finally { setBusy(false); }
  }, [email, days]);
  useEffect(() => { if (data) run(); /* refetch on range change once a user is loaded */ // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [days]);

  return (
    <div className="rounded-2xl border border-secondary-200 dark:border-secondary-800 p-5">
      <div className="mb-3 flex items-center justify-between gap-2">
        <h2 className="font-semibold">Dive into a user</h2>
        <RangeToggle value={days} onChange={setDays} />
      </div>
      <div className="flex gap-2 mb-3">
        <div className="relative flex-1">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-secondary-400" />
          <input value={email} onChange={(e) => setEmail(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && run()}
            type="email" placeholder="user@email.com" data-testid="user-email-input"
            className="w-full rounded-lg border border-secondary-200 dark:border-secondary-700 bg-white dark:bg-secondary-900 pl-8 pr-3 py-1.5 text-sm focus:border-secondary-400 focus:outline-none" />
        </div>
        <button onClick={run} disabled={busy}
          className="rounded-lg bg-secondary-900 dark:bg-white px-4 py-1.5 text-sm font-medium text-white dark:text-secondary-900 hover:bg-black disabled:opacity-50">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Analyze'}
        </button>
      </div>
      {err && <p className="text-sm text-amber-600 dark:text-amber-400">{err}</p>}
      {data && (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-4">
            <Stat label="Plan" value={data.user?.plan ?? '—'} />
            <Stat label="Requests" value={num(data.totals?.requests)} />
            <Stat label="Tokens" value={num(data.totals?.tokens)} />
            <Stat label="WP" value={data.user?.wp != null ? data.user.wp.toLocaleString() : '—'} />
          </div>
          <div className="mb-4"><TrafficBars buckets={data.dailyActivity} /></div>
          <TurnsTable rows={data.events} />
        </>
      )}
    </div>
  );
};

/** Admin Supervision: traffic analysis (7/30d), a masked prompt/response feed, and per-user drill-down. */
const SupervisionPanel: React.FC = () => (
  <>
    <TrafficSection />
    <UserDrill />
  </>
);

export default SupervisionPanel;
